import { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { PillButton } from '@ab-platform/ui/pill-button';
import { Banner } from '@ab-platform/ui/banner';
import { FocusedPageShell } from '@ab-platform/ui/focused-page-shell';
import { SelectField } from '@ab-platform/ui/select-field';
import { FieldError } from '@ab-platform/ui/field-error';
import { SoftBadge } from '@ab-platform/ui/soft-badge';
import {
  COMPANY_WITHHOLDING_TYPE_VALUES,
  STEP_FIELDS,
  type RegistrationFormValues,
  type WithholdingType,
} from '@/hooks/useRegistrationForm';
import { resumableAreaEnabled } from '@/lib/env';

const COMPANY_LABELS: Record<
  (typeof COMPANY_WITHHOLDING_TYPE_VALUES)[number],
  string
> = {
  ORDINARIA_23_50: 'Ordinaria — 23% su 50% imponibile',
  RIDOTTA_23_20: 'Ridotta — 23% su 20% imponibile',
  FORFETTARIO_0: 'Forfettario — esente',
};

/**
 * Step 5 — Info Fiscali. Withholding regime selection.
 *
 * PERSON  → locked to PRESTAZIONE_OCCASIONALE_20 (read-only chip).
 *           Set on mount via useEffect so the form passes the cross-
 *           field rule in `useRegistrationForm.ts` without the user
 *           having to interact.
 * COMPANY → SelectField with the 3 ordinary regimes. The "" / locked
 *           PRESTAZIONE_OCCASIONALE_20 is rejected by the schema for
 *           COMPANY; the FE only renders the 3 valid options.
 */
export function FiscalePage(): JSX.Element {
  const navigate = useNavigate();
  const form = useFormContext<RegistrationFormValues>();
  const { control, formState, trigger, setValue } = form;

  const legalNature = useWatch({ control, name: 'organization.legalNature' });
  const isPerson = legalNature === 'PERSON';
  const errors = formState.errors.organization?.withholdingType;

  // PERSON branch — auto-set the locked value the moment the user lands
  // here (or whenever they switch from COMPANY back to PERSON). The
  // cross-field rule rejects any other value, so leaving it untouched
  // would block submit silently.
  //
  // COMPANY branch — clear any leftover PRESTAZIONE_OCCASIONALE_20
  // value carried over from a previous PERSON pass (PR-E fix). Without
  // this reset, the SelectField would render the locked enum as raw
  // text because COMPANY_LABELS doesn't map it, AND the BE rejects the
  // payload because COMPANY can't use that regime.
  const currentWithholding = useWatch({
    control,
    name: 'organization.withholdingType',
  });
  useEffect(() => {
    if (isPerson) {
      setValue('organization.withholdingType', 'PRESTAZIONE_OCCASIONALE_20', {
        shouldValidate: true,
        shouldDirty: false,
      });
    } else if (currentWithholding === 'PRESTAZIONE_OCCASIONALE_20') {
      setValue('organization.withholdingType', '', {
        shouldValidate: false,
        shouldDirty: false,
      });
    }
  }, [isPerson, currentWithholding, setValue]);

  const handleContinue = async (): Promise<void> => {
    const ok = await trigger(
      STEP_FIELDS.fiscal as unknown as Parameters<typeof trigger>[0],
    );
    if (ok) navigate('/registrazione-intermediario/riepilogo');
  };

  return (
    <FocusedPageShell
      title="Informazioni fiscali"
      description={
        isPerson
          ? 'Per le persone fisiche la ritenuta è bloccata sul regime di prestazione occasionale.'
          : 'Indica il regime di ritenuta d\'acconto della società. Potrai aggiornarlo dal backoffice in seguito se cambia.'
      }
      onBack={() =>
        navigate(
          resumableAreaEnabled
            ? '/registrazione-intermediario/account'
            : '/registrazione-intermediario/amministratore',
        )
      }
      footer={
        <PillButton
          type="button"
          size="lg"
          onClick={handleContinue}
          className="w-full"
        >
          Continua
        </PillButton>
      }
    >
      {isPerson ? (
        <PersonLockedChip />
      ) : (
        <div className="grid gap-1.5">
          <Controller
            control={control}
            name="organization.withholdingType"
            render={({ field }) => {
              // Defense-in-depth against the carryover bug: if the form
              // somehow holds a value that doesn't belong on the COMPANY
              // branch (e.g. PRESTAZIONE_OCCASIONALE_20 leaked from a
              // previous PERSON pass before useEffect could fire), display
              // the placeholder rather than the raw enum string.
              const display = (
                COMPANY_WITHHOLDING_TYPE_VALUES as unknown as readonly string[]
              ).includes(field.value as string)
                ? (field.value as WithholdingType)
                : '';
              return (
                <SelectField
                  label="Ritenuta d'acconto"
                  value={display}
                  options={
                    COMPANY_WITHHOLDING_TYPE_VALUES as unknown as readonly string[]
                  }
                  renderOption={(value) =>
                    COMPANY_LABELS[
                      value as (typeof COMPANY_WITHHOLDING_TYPE_VALUES)[number]
                    ] ?? value
                  }
                  onChange={(next) => field.onChange(next)}
                  placeholder="—"
                />
              );
            }}
          />
          <FieldError message={errors?.message} />
        </div>
      )}
    </FocusedPageShell>
  );
}

function PersonLockedChip(): JSX.Element {
  return (
    <div className="grid gap-3">
      <Banner tone="info" title="Regime fiscale bloccato">
        Per la persona fisica la ritenuta d'acconto è impostata su
        <strong> Prestazione occasionale 20%</strong>. Non è modificabile da
        questo modulo: l'ufficio fiscale potrà aggiornarla dopo l'approvazione
        se cambieranno le condizioni.
      </Banner>
      <div className="flex items-center gap-2">
        <SoftBadge tone="info">Prestazione occasionale</SoftBadge>
        <SoftBadge tone="neutral">20%</SoftBadge>
      </div>
    </div>
  );
}
